// Golem fork (FT-1482): THE READER'S COLOUR — the one knob for the
// storyteller's colour app-wide.
//
// In clocktower.online purple was the storyteller's colour throughout the app
// (FT-1034 "purple is the storyteller's color app-wide"; FT-1150
// $control-edge-hover "the storyteller's own colour throughout the app"). In
// L.O.T.S. the Reader is GOLD. Rather than repaint a hundred hand-typed purples
// one at a time forever, the colour is published as CSS custom properties on the
// document root and every storyteller/Reader site reads them:
//
//   --reader-rgb          the base channels ("200, 162, 76") — for
//                         rgba(var(--reader-rgb), <alpha>) at any strength
//   --reader-color        rgb(var(--reader-rgb)) — the solid convenience
//   --reader-deep-rgb     the grimoire's own plum, moved to gold — the book's
//                         deep edge (was #4b3565)
//   --reader-ground-rgb   the near-black hover ground the purple edge sat on
//                         (was rgba(32, 24, 38, .95))
//
// So the Reader's colour is a single dial. The dev lab (App.vue's labs column)
// drives it; the pick persists per browser. Mirrors golem/coinArt's shape: a
// KEY, an observable choice, an apply() that paints the root, applied at import.
//
// NOTE ON WHAT IS *NOT* here: the TEAM colours ($traveler #cc04ff and the other
// five) are team identity, not the Reader — untouched. The "sealed/locked" role
// inks (golem/_roleInk.scss's #a78fcd family, the FT-1108 purple) are role
// identity, also untouched.
import Vue from "vue";

const KEY = "golem.readerColor";

// The fork's gold. Everything defaults here.
export const READER_GOLD = "#CD9C13";

// A few chips + a custom picker. Purple is kept so the old look can be put
// straight back; the rest are alternative Reader hues to try.
export const READER_PRESETS = [
  { id: "gold", label: "Gold", hex: READER_GOLD },
  { id: "amber", label: "Amber", hex: "#e0a63c" },
  { id: "rose", label: "Rose", hex: "#c76d8e" },
  { id: "teal", label: "Teal", hex: "#4cae9e" },
  { id: "sky", label: "Sky", hex: "#5b9bd6" },
  // the original clocktower.online storyteller purple ($control-edge-hover's
  // rgb(150, 130, 175)) — one click puts the whole app back to purple.
  { id: "purple", label: "Purple", hex: "#9682af" },
];

// ── colour maths (hex ⇄ rgb ⇄ hsl) ─────────────────────────────────────────
function hexToRgb(hex) {
  let h = String(hex).trim().replace(/^#/, "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHsl({ r, g, b }) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

const chan = ({ r, g, b }) => `${r}, ${g}, ${b}`;

// The deep and the ground keep the Reader's hue + saturation and drop only its
// lightness — the same ratios the purple family carried (deep ≈ ½ the base's
// lightness, ground ≈ ⅕ of it), so any picked hue derives a book edge and a
// hover ground that read as the same colour dimmed, exactly as purple did.
function derive(hex) {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);
  const deep = hslToRgb({ h: hsl.h, s: hsl.s, l: hsl.l * 0.5 });
  const ground = hslToRgb({ h: hsl.h, s: hsl.s, l: hsl.l * 0.2 });
  return { base: rgb, deep, ground };
}

function readStored() {
  try {
    const v = localStorage.getItem(KEY);
    if (v && /^#[0-9a-fA-F]{3,6}$/.test(v)) return v;
  } catch (e) {
    // storage off — fall through to the default
  }
  return READER_GOLD;
}

export const readerColorState = Vue.observable({ hex: readStored() });

/** Paint the Reader's colour onto the root so every storyteller site follows. */
export function applyReaderColor(hex) {
  const pick = /^#[0-9a-fA-F]{3,6}$/.test(String(hex || ""))
    ? hex
    : READER_GOLD;
  readerColorState.hex = pick;
  const { base, deep, ground } = derive(pick);
  const root = document.documentElement;
  root.style.setProperty("--reader-rgb", chan(base));
  root.style.setProperty("--reader-color", `rgb(${chan(base)})`);
  root.style.setProperty("--reader-deep-rgb", chan(deep));
  root.style.setProperty("--reader-ground-rgb", chan(ground));
  try {
    localStorage.setItem(KEY, pick);
  } catch (e) {
    // a browser with storage off still gets the swap for this session
  }
}

applyReaderColor(readerColorState.hex);
